// src/utils/consent.js
//
// The single owner of consent logic. Nothing else in the app may init GA4 or
// the Meta Pixel — it all goes through here.
//
// Compliance model: global opt-in. Nothing analytics/advertising fires until
// the visitor explicitly accepts. GA4 is not initialised until the analytics
// category is granted; the Pixel's script is not even loaded until the
// advertisement category is granted (deferring the load is what guarantees no
// _fbp/_fbc cookie appears beforehand — stronger than Meta's revoke pattern,
// which we can't verify keeps cookies off). Google Consent Mode v2 defaults
// (set in index.html) stay "denied" until a grant flips them.

import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import ReactGA from 'react-ga4';

const GA_ID = process.env.REACT_APP_GA4_ID || 'G-6DVXWSZC2L';
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '4367299196862647';

let gaInitialized = false;
let pixelLoaded = false;
let visitorCountry = null;
let currentPath =
  typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
const listeners = new Set();

// ---- Google Consent Mode v2 -------------------------------------------------

function gtagConsentUpdate(analytics, advertisement) {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: advertisement ? 'granted' : 'denied',
    ad_user_data: advertisement ? 'granted' : 'denied',
    ad_personalization: advertisement ? 'granted' : 'denied',
  });
}

// ---- Cookie cleanup on withdrawal -------------------------------------------

function eraseCookies(names) {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname.replace(/^www\./, '');
  names.forEach((base) => {
    // Match exact and prefixed (e.g. _ga, _ga_XXXX) across path/domain variants.
    document.cookie.split(';').forEach((c) => {
      const key = c.split('=')[0].trim();
      if (key === base || key.startsWith(base)) {
        for (const d of ['', `; domain=${host}`, `; domain=.${host}`]) {
          document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
        }
      }
    });
  });
}

// ---- GA4 --------------------------------------------------------------------

function initGA() {
  if (gaInitialized) return;
  ReactGA.initialize(GA_ID);
  gaInitialized = true;
  // The visit up to the moment of consent would otherwise be lost — send a
  // pageview for the route they are on right now.
  ReactGA.send({ hitType: 'pageview', page: currentPath });
}

// ---- Meta Pixel (loaded only on grant) --------------------------------------

function loadPixel() {
  if (pixelLoaded) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
  pixelLoaded = true;
}

function revokePixel() {
  if (window.fbq) {
    try {
      window.fbq('consent', 'revoke');
    } catch (e) {
      /* ignore */
    }
  }
  eraseCookies(['_fbp', '_fbc']);
}

// ---- Apply a consent state --------------------------------------------------

function apply(analytics, advertisement, action) {
  gtagConsentUpdate(analytics, advertisement);

  if (analytics) initGA();
  else eraseCookies(['_ga', '_gid']);

  if (advertisement) loadPixel();
  else revokePixel();

  listeners.forEach((fn) => {
    try {
      fn({ analytics, advertisement });
    } catch (e) {
      /* ignore */
    }
  });

  logConsent(action, analytics, advertisement);
}

// ---- Server-side audit record ----------------------------------------------

function logConsent(action, analytics, advertisement) {
  let consentId = null;
  try {
    const c = CookieConsent.getCookie();
    consentId = (c && (c.consentId || c.consent_id)) || null;
  } catch (e) {
    /* ignore */
  }
  try {
    fetch('/api/consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ consentId, action, analytics, advertisement, country: visitorCountry }),
    }).catch(() => {});
  } catch (e) {
    /* ignore */
  }
}

// ---- Public API -------------------------------------------------------------

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getConsent() {
  return {
    analytics: CookieConsent.acceptedCategory('analytics'),
    advertisement: CookieConsent.acceptedCategory('advertisement'),
  };
}

// Called by the router on every route change. Only forwards to tags that are
// actually live, so nothing fires for a category the visitor rejected.
export function trackPageview(path) {
  currentPath = path;
  if (gaInitialized) ReactGA.send({ hitType: 'pageview', page: path });
  if (pixelLoaded && window.fbq) window.fbq('track', 'PageView');
}

// Footer "Cookie Settings" link → reopen the preferences modal.
export function openPreferences() {
  CookieConsent.showPreferences();
}

// Ask the backend which regime applies from the visitor's IP. Fails safe: any
// error, or a slow response, defaults to opt-in (the stricter, compliant mode).
async function fetchRequireOptIn() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2000);
    const res = await fetch('/api/geo', { signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return true;
    const data = await res.json();
    visitorCountry = data.country || null;
    return data.requireOptIn !== false; // anything but an explicit false => opt-in
  } catch (e) {
    return true;
  }
}

// The opt-out notice needs a way to close without opening preferences. The
// library's consent modal has no built-in close icon, so we inject one, anchored
// to the visible box (.cm). Closing records "keep the defaults" (tracking stays
// on, as it already is) so the notice doesn't reappear on every page — the
// opt-out still lives in settings. Uses inline styles so it can't be defeated by
// CSS load order or specificity.
function addCloseX(modal) {
  try {
    if (!modal) return;
    // Anchor to the visible box (.cm). Only make it a positioning context if it
    // isn't already one — overriding the library's own positioning is what hid
    // the whole banner last time.
    const box = (modal.querySelector && modal.querySelector('.cm')) || modal;
    if (box.querySelector && box.querySelector('.cc-close-x')) return;
    const pos =
      typeof window !== 'undefined' && window.getComputedStyle
        ? window.getComputedStyle(box).position
        : 'static';
    if (pos === 'static') box.style.position = 'relative';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cc-close-x';
    btn.setAttribute('aria-label', 'Close');
    btn.textContent = '×';
    Object.assign(btn.style, {
      position: 'absolute', top: '8px', right: '10px',
      width: '34px', height: '34px', padding: '0', margin: '0',
      border: 'none', background: 'transparent', cursor: 'pointer',
      fontSize: '28px', lineHeight: '1', color: '#6b7280',
      borderRadius: '8px', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: '10',
    });
    btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(0,0,0,.07)'; btn.style.color = '#111827'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; btn.style.color = '#6b7280'; });
    btn.addEventListener('click', () => {
      try { CookieConsent.acceptCategory('all'); } catch (e) { /* ignore */ }
      try { CookieConsent.hide(); } catch (e) { /* ignore */ }
    });
    box.appendChild(btn);
  } catch (e) {
    /* ignore */
  }
}

function buildConfig(requireOptIn) {
  // opt-in (EU/UK/EEA/CH): nothing runs until accept.
  // opt-out (US, CA, AU, ...): analytics + advertising run by default, with a
  // clear way to turn them off. Legal there, and it recovers the ~half of
  // visitors that global opt-in was suppressing.
  const optionalEnabled = !requireOptIn;
  const offOrOn = requireOptIn ? 'Off until you accept.' : 'On by default — turn it off any time.';

  return {
    mode: requireOptIn ? 'opt-in' : 'opt-out',
    guiOptions: {
      // Both regions use the small box, bottom-left. EU/UK show Accept/Reject
      // on it; opt-out shows just "Cookie settings" plus a close X (added in
      // onModalReady) so it can be dismissed without opening preferences.
      consentModal: { layout: 'box', position: 'bottom left', flipButtons: false },
      preferencesModal: { layout: 'box' },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {
        enabled: optionalEnabled,
        readOnly: false,
        autoClear: { cookies: [{ name: /^(_ga|_gid)/ }] },
      },
      advertisement: {
        enabled: optionalEnabled,
        readOnly: false,
        autoClear: { cookies: [{ name: /^_fb(p|c)/ }] },
      },
    },
    onConsent: () => {
      // Fires on first choice AND on every load for a returning visitor. In
      // opt-out mode it also fires on the first load with the defaults, so US
      // tracking starts immediately without a click.
      apply(
        CookieConsent.acceptedCategory('analytics'),
        CookieConsent.acceptedCategory('advertisement'),
        'grant'
      );
    },
    onChange: () => {
      apply(
        CookieConsent.acceptedCategory('analytics'),
        CookieConsent.acceptedCategory('advertisement'),
        'change'
      );
    },
    onModalReady: ({ modalName, modal }) => {
      // Add the close X to the opt-out notice only. EU/UK must make an explicit
      // choice, so their banner has no dismiss.
      if (!requireOptIn && modalName === 'consentModal') addCloseX(modal);
    },
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'Your privacy',
            description: requireOptIn
              ? 'We use cookies to understand how visitors use this site and, with your permission, to measure our outreach. Nothing optional runs until you choose. See our <a href="/privacy-policy" class="cc__link">Privacy Policy</a>.'
              : 'We use cookies to understand how visitors use this site and to measure our outreach, so we can make it better for people going through the same thing. Manage your choices any time under Cookie settings. See our <a href="/privacy-policy" class="cc__link">Privacy Policy</a>.',
            // opt-in (EU/UK): accept + reject live on the banner, equal, as the
            // law requires. opt-out (US): neither is on the bar — both Accept
            // and Opt out sit equally inside Cookie settings, so the choice
            // stays symmetric (no privileged one-click) while the notice bar
            // stays slim.
            ...(requireOptIn ? { acceptAllBtn: 'Accept all', acceptNecessaryBtn: 'Reject all' } : {}),
            showPreferencesBtn: requireOptIn ? 'Manage preferences' : 'Cookie settings',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: requireOptIn ? 'Reject all' : 'Opt out',
            savePreferencesBtn: 'Save my choices',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'Strictly necessary',
                description:
                  'Required for the site to function — page navigation, the support chat, and remembering your cookie choice. Always on.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics',
                description: `Google Analytics (GA4), so we can see which content helps people and where the site can improve. ${offOrOn}`,
                linkedCategory: 'analytics',
              },
              {
                title: 'Advertising',
                description: `Meta Pixel, used to measure and improve our outreach. ${offOrOn}`,
                linkedCategory: 'advertisement',
              },
              {
                title: 'More information',
                description:
                  'Questions about your choices? Read our <a href="/privacy-policy" class="cc__link">Privacy Policy</a>.',
              },
            ],
          },
        },
      },
    },
  };
}

// Called once, at app mount. Async: we resolve the region first so tags never
// fire for an EU/UK visitor before we know they're opt-in.
export async function initConsent() {
  const requireOptIn = await fetchRequireOptIn();
  CookieConsent.run(buildConfig(requireOptIn));
}
